import { getFirebaseIdToken } from "@/shared/integrations/firebase/client";

/**
 * Minimal REST client for the robotic-imaging backend API (Cloud Run).
 *
 * Mirrors robotic-imaging-web's axios client (baseURL + Firebase Bearer token)
 * but uses native fetch to avoid adding axios/qs as dependencies. The backend
 * reads the Firebase `organization_id` claim from the token to scope responses,
 * so callers never pass an org id explicitly.
 */
const API_URL = import.meta.env.VITE_API_URL as string | undefined;
const API_BASE = API_URL ? `${API_URL}/api/v1` : undefined;

export type ApiQueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

export async function apiGet<T>(path: string, params?: ApiQueryParams): Promise<T> {
  if (!API_BASE) {
    throw new Error(
      "VITE_API_URL is not configured — cannot call the backend API.",
    );
  }

  const url = new URL(`${API_BASE}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const token = await getFirebaseIdToken();
  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API GET ${path} failed: ${res.status} ${res.statusText} ${body}`.trim());
  }

  return (await res.json()) as T;
}
